// src/utils/i18n-utils.ts
import { addLocale, getLocale as getLocaleByUmi, setLocale, useIntl } from 'umi'
import { getUrlParam } from './getValue'
import {
  getEventNameForLoadModuleDataFromMaster,
  MASTER_LOCALE_CHANGED,
  MICRO_LOCALE_CHANGED,
  MICRO_LOCALE_DATA_REQUEST,
} from '../constant/index'
import moment from 'moment'
import locales from '../locales'
import { getStorage } from './onStorage'
import { LangCacheReader } from './langCache'

export type ModuleResource = {
  [key: string]: {
    'zh-CN': string
    'en-US': string
    'zh-TW': string
  }
}

type Locale = 'zh-CN' | 'en-US' | 'zh-TW'

type ModuleResources = Record<string, ModuleResource>
type LocaleMessages = Record<string, string>
type ActiveModuleResources = Record<string, LocaleMessages>

type ModuleCacheReference = {
  module: string
  version: string
}

type IdbReadyPayload = {
  microName: string
  locale: Locale
  deliveryMode: 'idb-ready'
  modules: ModuleCacheReference[]
}

type ActiveLocaleFallbackPayload = {
  microName: string
  locale: Locale
  deliveryMode: 'active-locale-fallback'
  resources: ActiveModuleResources
}

type LegacyLocalePayload = {
  microName: string
  resources: ModuleResources
}

type LocalePayLoad =
  | IdbReadyPayload
  | ActiveLocaleFallbackPayload
  | LegacyLocalePayload

const langCache = new LangCacheReader()

const isIdbReadyPayload = (
  payload: LocalePayLoad,
): payload is IdbReadyPayload =>
  'deliveryMode' in payload && payload.deliveryMode === 'idb-ready'

const isActiveLocaleFallbackPayload = (
  payload: LocalePayLoad,
): payload is ActiveLocaleFallbackPayload =>
  'deliveryMode' in payload && payload.deliveryMode === 'active-locale-fallback'

/**
 * 添加带命名空间的国际化资源
 * @param module 模块名称
 * @param resource 模块资源（嵌套结构）
 */
export function addModuleLocales(module: string, resource: ModuleResource) {
  addModulesLocales({ [module]: resource })
}

/**
 * 将多个模块的语料合并后一次性注册，避免每个模块都触发当前语言刷新。
 */
export function addModulesLocales(moduleResources: ModuleResources) {
  const { locale: _locale } = getLocaleByPriority()
  const localeResources: Record<Locale, Record<string, string>> = {
    'zh-CN': {} as Record<string, string>,
    'en-US': {} as Record<string, string>,
    'zh-TW': {} as Record<string, string>,
  }

  Object.entries(moduleResources).forEach(([module, resource]) => {
    Object.entries(resource).forEach(([key, translations]) => {
      const namespacedKey = module ? `${module}:${key}` : key
      localeResources['zh-CN'][namespacedKey] = translations['zh-CN']
      localeResources['en-US'][namespacedKey] = translations['en-US']
      localeResources['zh-TW'][namespacedKey] = translations['zh-TW']
    })
  })
  ;(
    Object.entries(localeResources) as [Locale, Record<string, string>][]
  ).forEach(([locale, resources]) => {
    addLocale(locale, resources, {
      momentLocale: locale.split('-')[1].toLowerCase(),
      antd: _locale,
    })
  })
}

/** 注册主应用投递的当前语种资源。 */
export function addModulesLocale(
  locale: Locale,
  moduleResources: ActiveModuleResources,
) {
  const { locale: antdLocale } = getLocaleByPriority()
  const resources: LocaleMessages = {}

  Object.entries(moduleResources).forEach(([module, messages]) => {
    Object.entries(messages).forEach(([key, message]) => {
      resources[module ? `${module}:${key}` : key] = message
    })
  })

  addLocale(locale, resources, {
    momentLocale: locale.split('-')[1].toLowerCase(),
    antd: antdLocale,
  })
}

async function getCachedActiveResources(
  modules: ModuleCacheReference[],
  locale: Locale,
): Promise<ActiveModuleResources | null> {
  const cachedModules = await Promise.all(
    modules.map(async ({ module, version }) => {
      const cached = await langCache.get(module)
      if (!cached || cached.version !== version) return null

      const messages: LocaleMessages = {}
      Object.entries(cached.resources).forEach(([key, translations]) => {
        const message = translations[locale]
        if (typeof message === 'string') messages[key] = message
      })
      return { module, messages }
    }),
  )

  if (cachedModules.some((item) => !item)) return null

  return cachedModules.reduce<ActiveModuleResources>((resources, item) => {
    if (item) resources[item.module] = item.messages
    return resources
  }, {})
}

export function initModuleLang(props: {
  eventCenter: any
  name: string
}): () => void {
  if (!props || !props.eventCenter || !props.name) return () => {}
  const { eventCenter, name } = props
  const eventName = getEventNameForLoadModuleDataFromMaster(name)
  let isUnmounted = false
  let hasAppliedResources = false
  let isApplyingResources = false
  let hasRequestedFallback = false

  const requestFallback = (locale: Locale) => {
    if (isUnmounted || hasAppliedResources || hasRequestedFallback) return
    hasRequestedFallback = true
    eventCenter.emit(MICRO_LOCALE_DATA_REQUEST, {
      microName: name,
      locale,
      deliveryMode: 'active-locale-fallback',
    })
  }

  const handler = (payload: LocalePayLoad) => {
    if (isUnmounted || hasAppliedResources || !payload) return

    if (isIdbReadyPayload(payload)) {
      if (isApplyingResources || hasRequestedFallback) return
      isApplyingResources = true
      void getCachedActiveResources(payload.modules, payload.locale)
        .then((resources) => {
          if (isUnmounted || hasAppliedResources) return
          if (!resources) {
            requestFallback(payload.locale)
            return
          }
          addModulesLocale(payload.locale, resources)
          hasAppliedResources = true
        })
        .catch(() => requestFallback(payload.locale))
        .finally(() => {
          isApplyingResources = false
        })
      return
    }

    if ('resources' in payload) {
      if (isActiveLocaleFallbackPayload(payload)) {
        addModulesLocale(payload.locale, payload.resources)
      } else {
        addModulesLocales(payload.resources)
      }
      hasAppliedResources = true
    }
  }
  eventCenter.on(eventName, handler)

  const { locale } = getLocaleByPriority()
  eventCenter.emit(MICRO_LOCALE_DATA_REQUEST, {
    microName: name,
    locale: locale as Locale,
    deliveryMode: 'idb-ready',
  })

  return () => {
    isUnmounted = true
    eventCenter.off(eventName, handler)
  }
}

// 判断是否是 Qiankun 子应用
const isMicroApp = !!window.__POWERED_BY_QIANKUN__

// 接收主应用通知语言改变
export function onMasterLangChange(reload: boolean = true) {
  window.eventCenter.on(
    MASTER_LOCALE_CHANGED,
    function ({ lang }: { lang: string }) {
      // setLocale(lang, reload)
    },
  )

  return () => {
    window.eventCenter.off(MASTER_LOCALE_CHANGED)
  }
}

// 主应用通知子应用语言改变
export function setLangLocale(locale = getLocaleByPriority(), reload = true) {
  setLocale(locale, reload)
  if (isMicroApp) {
    window.eventCenter.emit(MICRO_LOCALE_CHANGED, { locale })
  } else {
    window.eventCenter.emit(MASTER_LOCALE_CHANGED, {
      locale,
      app: window.__QIANKUN_APP_NAME__,
    })
  }
}

export function getLocaleByPriority() {
  const localeFromUmiLocal = localStorage.getItem('umi_locale')
  const localeFromBossLocal = sessionStorage.getItem('lg')
  const tenantFromLocal = getStorage('tenantCode') || ''
  const localeFromUrl = getUrlParam('lg')
  const tenantCodeFromUrl = getUrlParam('tenantCode') || ''
  const locale =
    localeFromUrl || localeFromUmiLocal || localeFromBossLocal || 'zh-CN'
  // 非标
  const bossLocale = transLangKeysForBoss(locale)
  // Saas 标准化语种
  const standardLocale = transLangKeysForStandard(locale)

  // 设置默认语种优先级别 url > browser local > defaultLocaleFromApiConfig
  return {
    locale: standardLocale,
    bossLocale,
    isNeedResetLocale:
      (tenantCodeFromUrl &&
        tenantFromLocal &&
        tenantFromLocal !== tenantCodeFromUrl) ||
      (!localeFromUmiLocal && !localeFromUrl),
  }
}

export function convertToUpperCaseWithRegex(str: string) {
  if (!str) return ''
  return str.replace(/-(\w+)/, function (_, p1) {
    return '-' + p1.toUpperCase()
  })
}

// 把boss 的语种转化为 Saas 标准化
export function transLangKeysForStandard(locale: string) {
  if (!locale) return ''
  const lowerLocale = locale.toLowerCase()
  const langMaps = {
    'zh-cn': 'zh-CN',
    en: 'en-US',
    'zh-tw': 'zh-TW',
  }

  if (lowerLocale in langMaps) {
    return langMaps[lowerLocale as keyof typeof langMaps]
  } else {
    return convertToUpperCaseWithRegex(locale)
  }
}

export function transLangKeysForBoss(locale: string) {
  if (!locale) return ''
  const langMaps = {
    'zh-CN': 'zh-cn',
    'en-US': 'en',
    'zh-TW': 'zh-tw',
    'ja-JP': 'jp',
  }
  if (locale in langMaps) {
    return langMaps[locale as keyof typeof langMaps]
  } else {
    return convertToUpperCaseWithRegex(locale)
  }
}

export function setLocaleForApp(props: {
  locale: string
  isReload?: boolean
  forceUpdateWindowLang?: boolean
}) {
  const { locale, forceUpdateWindowLang = false } = props
  if (!locale) return
  const localeFromLocal = localStorage.getItem('umi_locale')
  const localeFromBossLocal = sessionStorage.getItem('lg')
  const bossLocale = transLangKeysForBoss(locale)
  const standardLocale = transLangKeysForStandard(locale)

  // Saas 标准化语种
  if (standardLocale !== localeFromLocal) {
    console.log('standardSassLocale: ', locale)
    localStorage.setItem('umi_locale', standardLocale)
    // const isReload = 'isReload' in props ? !!props.isReload : (standardLocale !== 'zh-CN' && !!localeFromLocal)
    const isReload =
      'isReload' in props ? !!props.isReload : standardLocale !== 'zh-CN'
    console.log('isReload: ', isReload, standardLocale)
    setLocale(standardLocale, isReload)
  }

  // boss 语种
  if (forceUpdateWindowLang || localeFromBossLocal !== bossLocale) {
    console.log('setBossLocale: ', bossLocale)
    setWindowLang(bossLocale)
    sessionStorage.setItem('lg', bossLocale)
  }
}

export const setWindowLang = async (languages: string) => {
  const _languages = (languages || 'zh-CN').toLocaleLowerCase()
  if (['zh-cn', 'zh-CN'].includes(_languages)) {
    moment.locale('zh-cn')
  }
  window.lang = new Proxy(locales?.[_languages] || {}, {
    get(target, name) {
      return target?.[name] || name
    },
  })
}
